/**
 * 图片加载逻辑
 */
import { MAX_CONCURRENT_LOAD_TASKS } from '../utils/constants';
import ImageReloader from '@/components/st_comps/st_comp/utils/ImageReloader';

/**
 * 判断是否为审核目标图片
 * @param {*} image - 图片对象
 * @returns {boolean}
 */
function isAuditTarget(image) {
  return Boolean(image && typeof image === 'object' && image.im);
}

export function useImageLoader() {
  return {
    /**
     * 开始加载图片队列
     * @param {Array} images - 图片数组
     * @param {object} options - 选项
     * @param {Function} options.onUpdate - 单张图片加载完成回调 (image, imageObject, isError) => void
     * @param {Function} options.onProgress - 进度更新回调 (finishedCount, totalCount) => void
     * @param {Function} options.onStatusChange - 状态文本变化回调 (text) => void
     * @param {Function} options.onEnd - 全部加载完成回调 () => void
     * @param {Function} options.onReloadPolling - 存在 errorImages 时回调 (errorImages) => void
     * @param {Function} options.onAbnormal - 加载异常回调 (err, image) => void
     * @param {object} tokenRef - 用于取消加载的 token 引用 { current: number }
     * @param {Function} isTokenValid - 判断 token 是否有效的函数 (currentToken) => boolean
     * @returns {object} { loadToken }
     */
    startLoadQueue(
      images,
      { onUpdate, onProgress, onStatusChange, onEnd, onReloadPolling, onAbnormal },
      tokenRef,
      isTokenValid
    ) {
      const loadQueue = Array.isArray(images) ? images.map((image, index) => ({ image, index })) : [];
      const loadToken = Date.now();
      tokenRef.current = loadToken;

      if (!loadQueue.length) {
        if (typeof onEnd === 'function') {
          onEnd();
        }
        return { loadToken };
      }

      let runningCount = 0;
      let nextTaskIndex = 0;
      let loadFinishedCount = 0;
      const loadTotalCount = loadQueue.length;
      const errorImages = [];

      const canContinue = () => isTokenValid(loadToken);

      const finalizeTask = () => {
        if (!canContinue()) {
          return;
        }

        runningCount = runningCount > 0 ? runningCount - 1 : 0;
        loadFinishedCount++;

        if (typeof onProgress === 'function') {
          onProgress(loadFinishedCount, loadTotalCount);
        }

        if (loadFinishedCount === loadTotalCount && runningCount === 0 && nextTaskIndex >= loadQueue.length) {
          if (errorImages.length > 0) {
            if (typeof onReloadPolling === 'function') {
              onReloadPolling(errorImages);
            }
            return;
          }
          if (typeof onEnd === 'function') {
            onEnd();
          }
          return;
        }

        pumpQueue();
      };

      const handleLoadComplete = (image, imageObject, isError) => {
        if (!canContinue()) {
          return;
        }

        if (isError) {
          // 阶段1：加载失败，收集 errorImages，不设置 orisize
          errorImages.push({
            image: image,
            imageObject: imageObject,
            url: image.im || image.src || image
          });
          finalizeTask();
          return;
        }

        if (isAuditTarget(image)) {
          image.orisize = [imageObject.width, imageObject.height];
        }

        if (typeof onUpdate === 'function') {
          onUpdate(image, imageObject, false);
        }
        finalizeTask();
      };

      const runLoadTask = (image) => {
        let im = null;
        try {
          if (typeof onStatusChange === 'function') {
            onStatusChange('正在加载图片');
          }

          const imageUrl = isAuditTarget(image) ? image.im : image;
          im = new Image();

          im.onload = function () {
            handleLoadComplete(image, this, false);
          };

          im.onerror = function () {
            handleLoadComplete(image, this, true);
          };

          im.src = imageUrl;
        } catch (err) {
          if (typeof onAbnormal === 'function') {
            onAbnormal(err, image);
          }
          // 异常也视为加载失败，避免卡死
          handleLoadComplete(image, im || {}, true);
        }
      };

      const pumpQueue = () => {
        if (!canContinue()) {
          return;
        }

        while (runningCount < MAX_CONCURRENT_LOAD_TASKS && nextTaskIndex < loadQueue.length) {
          const task = loadQueue[nextTaskIndex];
          nextTaskIndex++;
          runningCount++;
          runLoadTask(task.image);
        }
      };

      pumpQueue();

      return { loadToken };
    },

    /**
     * 阶段2：轮询重载
     * @param {Array} imagesToReload - errorImages
     * @param {Object} options - { onUpdate, onProgress, onEnd, onAttempt, onAbnormal }
     * @returns {ImageReloader} - 返回实例，便于外部 destroy
     */
    reloadImagesWithPolling(imagesToReload, { onUpdate, onEnd, onAttempt, onAbnormal }) {
      const reloader = new ImageReloader({
        pollInterval: 10000,
        maxPollAttempts: 30
      });

      reloader.reloadBatch(imagesToReload, {
        onItemSuccess: (item, imageObject) => {
          if (typeof onUpdate === 'function') {
            onUpdate(item.image, imageObject, false);
          }
        },
        onItemError: (item) => {
          if (typeof onUpdate === 'function') {
            // 超时已设置 orisize=[300,300]，imageObject 为阶段1 的实例
            onUpdate(item.image, item.imageObject, true);
          }
        },
        onAttempt: (item, attempts, isSuccess, timedOut) => {
          if (typeof onAttempt === 'function') {
            onAttempt(item, attempts, isSuccess, timedOut);
          }
        }
      }).then(() => {
        reloader.destroy();
        if (typeof onEnd === 'function') {
          onEnd();
        }
      }).catch((err) => {
        reloader.destroy();
        if (typeof onAbnormal === 'function') {
          onAbnormal(err);
        }
        if (typeof onEnd === 'function') {
          onEnd();
        }
      });

      return reloader;
    }
  };
}
